import {
	getPaddockQueryKey,
	getPaddockQueryOptions,
} from "@packages/api-client/hooks/paddockController";
import {
	getPaddockSoilReferenceQueryOptions,
	listSoilAnalysesQueryOptions,
} from "@packages/api-client/hooks/soilAnalysisController";
import { Action } from "@packages/shared-types";
import {
	interpretSoilAnalysis,
	type SoilReferenceProfile,
} from "@packages/shared-types/grass";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ListPlus, MoreVertical, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeletePaddock } from "@/api/grass";
import { useListMedia } from "@/api/media";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { PaddockGrazingReadiness } from "@/components/grass/PaddockGrazingReadiness";
import type { SoilAnalysisInitial } from "@/components/grass/soil/SoilAnalysisForm";
import { SoilAnalysisSection } from "@/components/grass/soil/SoilAnalysisSection";
import { ImageUploadDialog } from "@/components/media/ImageUploadDialog";
import { MediaGallery } from "@/components/media/MediaGallery";
import { OfflineActionGuard } from "@/components/OfflineActionGuard";
import { OfflineUnavailable } from "@/components/OfflineUnavailable";
import { QuickAddTask } from "@/components/planning";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { DetailSkeleton } from "@/components/ui/skeleton";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useOfflineGuard } from "@/hooks/useOfflineGuard";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { findOfflinePaddockEntry } from "@/hooks/usePaddocksListData";
import { PaddockStatusPill } from "../../../../-components/grassPresentation";
import { GrassRegisterPicker } from "./-components/GrassRegisterPicker";
import { GrazingCard } from "./-components/GrazingCard";
import { PaddockAside, PaddockBoundaryCard } from "./-components/PaddockAside";
import { PaddockCultivarsCard } from "./-components/PaddockCultivarsCard";
import { PaddockDetailOffline } from "./-components/PaddockDetailOffline";
import { PaddockIdentityCard } from "./-components/PaddockIdentityCard";
import { PaddockKpiCard } from "./-components/PaddockKpiCard";
import {
	PaddockMobileEditButton,
	PaddockMobileTopBar,
} from "./-components/PaddockMobileChrome";
import { PaddockRecordsCard } from "./-components/PaddockRecordsCard";
import {
	PaddockSection,
	scrollToPaddockSection,
} from "./-components/PaddockSection";
import { usePaddockStocking } from "./-components/paddockStocking";
import { usePaddockKpis } from "./-components/usePaddockKpis";

/** From `sm` the breadcrumb header earns its row; below it the page becomes
 *  the phone layout (a 56px identity bar, as the animal page), not a squeezed
 *  header. Chosen by viewport rather than CSS visibility so the phone never
 *  carries a hidden copy of the desktop header (duplicate title, twice the
 *  DOM). */
const DESKTOP_QUERY = "(min-width: 640px)";

/** Section ids double as `#hash` targets, so `…/paddocks/<id>#soil` lands on
 *  the soil work. */
const SECTION = {
	pasture: "pasture",
	records: "records",
	soil: "soil",
	boundary: "boundary",
	images: "images",
} as const;

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/grass-production/farms/$farmId/paddocks/$paddockId/",
)({
	staticData: { offline: "ready" },
	component: PaddockDetailPage,
});

function PaddockDetailPage() {
	const { orgId, farmId, paddockId } = Route.useParams();
	const online = useOnlineStatus();
	const queryClient = useQueryClient();

	if (!online) {
		const cached = queryClient.getQueryState(
			getPaddockQueryKey(orgId, farmId, paddockId),
		);
		const entry = findOfflinePaddockEntry(
			queryClient,
			orgId,
			farmId,
			paddockId,
		);
		// A write refreshes the prepared row but leaves the unmounted `getPaddock`
		// snapshot as it was, so the snapshot has to lose once it is older.
		const preparedIsNewer =
			!!entry && entry.preparedAt > (cached?.dataUpdatedAt ?? 0);
		if (!cached?.data || preparedIsNewer) {
			// Prepared dataset row → offline read-only view; nothing at all →
			// the same "prepare in the Offline Center" panel a guard would show.
			return entry ? (
				<PaddockDetailOffline row={entry.row} />
			) : (
				<OfflineUnavailable variant="preparable" />
			);
		}
	}

	return <PaddockDetailFull />;
}

function PaddockDetailFull() {
	const { orgId, farmId, paddockId } = Route.useParams();
	const { t } = useTranslation();
	// The stance only gates navigation; a user who loaded this screen online and
	// then lost connectivity stays on it — the un-queueable write triggers
	// (edit/upload/task) need their own gate.
	const { blocked } = useOfflineGuard();

	const checkPermission = useHasPermission();
	// Grass splits view/create by record type — the KPI row, records feed, and
	// picker degrade to what the caller is allowed to see/do.
	const canViewOperations = checkPermission(Action.VIEW_OPERATIONS);
	const canViewMeasurements = checkPermission(Action.VIEW_MEASUREMENTS);
	const canCreateOperations = checkPermission(Action.CREATE_OPERATIONS);
	// Also gates measurement edits: the update route enforces CREATE, not MANAGE.
	const canCreateMeasurements = checkPermission(Action.CREATE_MEASUREMENTS);
	const canManageOperations = checkPermission(Action.MANAGE_OPERATIONS);
	const canManageMeasurements = checkPermission(Action.MANAGE_MEASUREMENTS);
	// Same action the backend DELETE route enforces — hiding the entry keeps a
	// viewer from reaching a guaranteed 403.
	const canManagePaddocks = checkPermission(Action.MANAGE_PADDOCKS);

	const [deleteOpen, setDeleteOpen] = useState(false);
	const isDesktop = useMediaQuery(DESKTOP_QUERY);

	const navigate = Route.useNavigate();
	const deletePaddock = useDeletePaddock();

	const { data, isLoading } = useQuery(
		getPaddockQueryOptions(orgId, farmId, paddockId),
	);

	const { data: soilAnalysesData } = useQuery(
		listSoilAnalysesQueryOptions(orgId, farmId, paddockId),
	);
	const { data: soilReferenceData } = useQuery(
		getPaddockSoilReferenceQueryOptions(orgId, farmId, paddockId),
	);
	const soilReference = soilReferenceData?.profiles.find(
		(profile) => profile.id === soilReferenceData.selectedProfileId,
	) as SoilReferenceProfile | undefined;

	const kpis = usePaddockKpis(orgId, farmId, paddockId, {
		canViewMeasurements,
		canViewOperations,
	});
	const stocking = usePaddockStocking(
		orgId,
		farmId,
		paddockId,
		data?.areaHa ?? 0,
	);

	// Interpret the most recent dated analysis once — feeds the aside soil
	// summary (nulls sort last).
	const { soil, soilSampledAt } = useMemo(() => {
		const rows = (soilAnalysesData?.data ??
			[]) as unknown as SoilAnalysisInitial[];
		if (rows.length === 0) return { soil: undefined, soilSampledAt: undefined };
		const latest = [...rows].sort((a, b) =>
			(b.sampledAt ?? "").localeCompare(a.sampledAt ?? ""),
		)[0];
		return {
			soil: soilReference
				? interpretSoilAnalysis(latest.results, { reference: soilReference })
				: undefined,
			soilSampledAt: latest.sampledAt,
		};
	}, [soilAnalysesData, soilReference]);

	// A `#soil`-style link lands on its section once the page has content to
	// land on (the router's own hash scroll fires before the paddock loads).
	const loaded = Boolean(data);
	useEffect(() => {
		const id = window.location.hash.slice(1);
		if (loaded && id) scrollToPaddockSection(id);
	}, [loaded]);

	if (isLoading) {
		return <DetailSkeleton />;
	}

	if (!data) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">
					{t("grass.paddock.detail.notFound")}
				</p>
			</div>
		);
	}

	const editLink = {
		to: "/app/org/$orgId/grass-production/farms/$farmId/paddocks/$paddockId/edit",
		params: { orgId, farmId, paddockId },
	} as const;

	// This route's entity is gone once the delete lands, so leave for the
	// farm-scoped grass hub (the same target as the mobile back link) instead of
	// letting the invalidated paddock query refetch into a 404.
	const handleConfirmDelete = () => {
		deletePaddock.mutate(
			{ organizationId: orgId, establishmentId: farmId, paddockId },
			{
				onSuccess: () => {
					setDeleteOpen(false);
					navigate({
						to: "/app/org/$orgId/grass-production",
						params: { orgId },
						search: { farm: farmId },
					});
				},
			},
		);
	};

	// One set of blocks, two arrangements (mirrors the animal page): the
	// overflow menu, the register picker, the sections and the delete dialog
	// are built once and placed by whichever header the viewport picks.

	// Secondary actions collapse under an overflow menu — the same trigger, menu
	// and items on phone and desktop so the header does not grow a second,
	// breakpoint-specific overflow.
	const moreMenu = (
		<div className="dropdown dropdown-end">
			<Button
				tabIndex={0}
				variant="ghost"
				size="icon"
				className="h-11 w-11 shrink-0"
				aria-label={t("grass.paddock.actions.more")}
			>
				<MoreVertical className="h-5 w-5" />
			</Button>
			<ul
				className="menu dropdown-content z-10 mt-1 w-56 rounded-box border border-base-300 bg-base-100 p-2 shadow-lg"
				// biome-ignore lint/a11y/noNoninteractiveTabindex: DaisyUI dropdown pattern focuses the content list to keep it open.
				tabIndex={0}
			>
				<li>
					<OfflineActionGuard blocked={blocked} className="w-full">
						<ImageUploadDialog
							orgId={orgId}
							entityType="GrassOptzPaddock"
							entityId={paddockId}
							trigger={
								<button
									type="button"
									aria-disabled={blocked}
									className="flex w-full items-center gap-2"
								>
									<Upload className="h-4 w-4" />
									{t("media.upload.button")}
								</button>
							}
						/>
					</OfflineActionGuard>
				</li>
				<li>
					<OfflineActionGuard blocked={blocked} className="w-full">
						<QuickAddTask
							organizationId={orgId}
							establishmentId={farmId}
							domain="grass"
							domainData={{ paddockId }}
							trigger={
								<button
									type="button"
									aria-disabled={blocked}
									className="flex w-full items-center gap-2"
								>
									<ListPlus className="h-4 w-4" />
									{t("planning.tasks.quickAdd")}
								</button>
							}
						/>
					</OfflineActionGuard>
				</li>
				{canManagePaddocks && (
					<li>
						<OfflineActionGuard blocked={blocked} className="w-full">
							<button
								type="button"
								aria-disabled={blocked}
								onClick={() => setDeleteOpen(true)}
								className="flex w-full items-center gap-2 text-error"
							>
								<Trash2 className="h-4 w-4" />
								{t("grass.paddock.actions.delete")}
							</button>
						</OfflineActionGuard>
					</li>
				)}
			</ul>
		</div>
	);

	// Verb-first "+ Registrar" lives on the Records card that owns the write
	// (as the animal page keeps Record on its Records section) — the same
	// element on phone and desktop, so neither header grows a create button.
	const registerPicker = (
		<GrassRegisterPicker
			orgId={orgId}
			farmId={farmId}
			paddockId={paddockId}
			canCreateOperations={canCreateOperations}
			canCreateMeasurements={canCreateMeasurements}
			size="sm"
		/>
	);

	const aside = (
		<PaddockAside
			paddock={data}
			soil={soil}
			soilSampledAt={soilSampledAt}
			orgId={orgId}
			farmId={farmId}
			paddockId={paddockId}
			onOpenSoil={() => scrollToPaddockSection(SECTION.soil)}
		/>
	);

	const cultivarsCard = (
		<PaddockCultivarsCard
			cultivars={data.cultivars}
			orgId={orgId}
			farmId={farmId}
			paddockId={paddockId}
			canManagePaddocks={canManagePaddocks}
			blocked={blocked}
		/>
	);

	// One scrolling page, ordered by how often each part is
	// reached for: the pasture's state, the day-to-day records, then the
	// periodic soil work, the boundary and the photos. On a phone the cultivars
	// and quality targets join the column; the desktop keeps them in the rail.
	const sections = (withRail: boolean) => (
		<>
			<PaddockSection id={SECTION.pasture}>
				<PaddockKpiCard
					kpis={kpis}
					readiness={data.grazingReadiness}
					stocking={stocking}
					herds={
						<GrazingCard orgId={orgId} farmId={farmId} paddockId={paddockId} />
					}
				/>
			</PaddockSection>
			{withRail && cultivarsCard}
			<PaddockSection id={SECTION.records}>
				<PaddockRecordsCard
					orgId={orgId}
					farmId={farmId}
					paddock={data}
					canViewOperations={canViewOperations}
					canViewMeasurements={canViewMeasurements}
					canEditOperations={canManageOperations}
					canEditMeasurements={canCreateMeasurements}
					canDeleteOperations={canManageOperations}
					canDeleteMeasurements={canManageMeasurements}
					action={registerPicker}
				/>
			</PaddockSection>
			<PaddockSection id={SECTION.soil}>
				<SoilAnalysisSection
					soilAnalysesData={soilAnalysesData}
					orgId={orgId}
					farmId={farmId}
					paddockId={paddockId}
				/>
			</PaddockSection>
			<PaddockSection id={SECTION.boundary}>
				<PaddockBoundaryCard
					paddock={data}
					orgId={orgId}
					farmId={farmId}
					paddockId={paddockId}
				/>
			</PaddockSection>
			<PaddockSection id={SECTION.images}>
				<ImagesCard orgId={orgId} paddockId={paddockId} blocked={blocked} />
			</PaddockSection>
			{withRail && (
				<PaddockAside
					paddock={data}
					orgId={orgId}
					farmId={farmId}
					paddockId={paddockId}
					showSoilSummary={false}
				/>
			)}
		</>
	);

	const deleteDialog = (
		<ConfirmDeleteDialog
			open={deleteOpen}
			title={t("grass.paddock.deleteConfirm.title")}
			description={t("grass.paddock.deleteConfirm.description", {
				name: data.name,
			})}
			onCancel={() => setDeleteOpen(false)}
			onConfirm={handleConfirmDelete}
			isPending={deletePaddock.isPending}
		/>
	);

	const useTypeLabel = t(`grass.paddock.useTypes.${data.useType}`);
	const areaLabel = `${data.areaHa?.toFixed(2)} ha`;
	const occupied = stocking ? stocking.head > 0 : undefined;
	const isGrazingPaddock = !["HAY", "SILAGE", "CUT_AND_CARRY"].includes(
		data.useType,
	);

	// --- Desktop ------------------------------------------------------------
	// The animal page's arrangement: breadcrumb and actions share one row, a
	// 300px identity rail on the left, the working column on the right.
	if (isDesktop) {
		return (
			// The shell is height-locked (`html,body{overflow:hidden}` + an
			// `h-[100dvh]` wrapper), so the page owns its scroll.
			<div className="flex h-full flex-col gap-5 overflow-y-auto pb-10">
				<div className="flex items-center justify-between gap-4">
					<Breadcrumb
						items={[
							{
								label: t("grass.nav.title"),
								to: "/app/org/$orgId/grass-production",
								params: { orgId },
							},
							{
								label: t("grass.farm.title"),
								to: "/app/org/$orgId/grass-production",
								params: { orgId },
								search: { farm: farmId },
							},
							{ label: data.name },
						]}
					/>
					<div className="flex shrink-0 items-center gap-2.5">
						<OfflineActionGuard blocked={blocked}>
							<Button
								type="button"
								variant="outline"
								size="touch"
								aria-disabled={blocked}
								onClick={() => navigate(editLink)}
							>
								{t("common.edit")}
							</Button>
						</OfflineActionGuard>
						{moreMenu}
					</div>
				</div>

				<div className="grid grid-cols-[300px_minmax(0,1fr)] items-start gap-5">
					<div className="flex min-w-0 flex-col gap-5">
						<PaddockIdentityCard
							paddock={data}
							occupied={occupied}
							addLink={(label) => (
								<OfflineActionGuard blocked={blocked} className="inline-flex">
									<Link
										{...editLink}
										aria-disabled={blocked}
										className="text-xs font-semibold text-primary hover:underline"
									>
										{label}
									</Link>
								</OfflineActionGuard>
							)}
						/>
						{cultivarsCard}
						{aside}
					</div>
					<div className="flex min-w-0 flex-col gap-5">{sections(false)}</div>
				</div>
				{deleteDialog}
			</div>
		);
	}

	// --- Mobile -------------------------------------------------------------
	// Back goes to the farm-scoped paddock list. ?farm= preserved so the list
	// reopens on the same farm scope (see farm-scope gotcha in CLAUDE.md).
	const goBack = () =>
		navigate({
			to: "/app/org/$orgId/grass-production",
			params: { orgId },
			search: { farm: farmId },
		});

	return (
		<div className="flex flex-col gap-3.5">
			<PaddockMobileTopBar
				title={data.name}
				code={data.code}
				subtitle={useTypeLabel}
				onBack={goBack}
				editAction={
					<OfflineActionGuard blocked={blocked}>
						<PaddockMobileEditButton
							onClick={() => navigate(editLink)}
							blocked={blocked}
						/>
					</OfflineActionGuard>
				}
				moreMenu={moreMenu}
			/>

			{/* Badge row: status leads (it is the one fact that changes), then the
			    area. The use type already rides the bar's sub-line. */}
			<div className="flex flex-wrap items-center gap-1.5">
				<PaddockStatusPill status={data.status} size="sm" />
				<Badge variant="outline" className="font-mono tabular-nums">
					{areaLabel}
				</Badge>
			</div>
			{isGrazingPaddock && (
				<PaddockGrazingReadiness
					readiness={data.grazingReadiness}
					occupied={occupied}
				/>
			)}

			{/* No sticky action bar to clear; `<main>` already reserves the
			    floating navigation pill's own height below the page. */}
			{sections(true)}
			{deleteDialog}
		</div>
	);
}

function ImagesCard({
	orgId,
	paddockId,
	blocked,
}: {
	orgId: string;
	paddockId: string;
	blocked: boolean;
}) {
	const { t } = useTranslation();
	const { data: mediaData } = useListMedia(orgId, {
		entityType: "GrassOptzPaddock",
		entityId: paddockId,
	});
	const imageCount = mediaData?.data?.length ?? 0;
	const hasImages = imageCount > 0;

	return (
		<div className="card bg-base-100 shadow-sm">
			<div className="card-body gap-3 p-4">
				<div className="flex items-center justify-between">
					<h3 className="font-display text-base font-semibold">
						{t("grass.paddock.images.title")}{" "}
						<span className="font-normal text-base-content/50">
							({imageCount})
						</span>
					</h3>
					<OfflineActionGuard blocked={blocked}>
						<ImageUploadDialog
							orgId={orgId}
							entityType="GrassOptzPaddock"
							entityId={paddockId}
							trigger={
								<Button variant="outline" size="sm" aria-disabled={blocked}>
									<Upload className="mr-2 h-4 w-4" />
									{t("grass.paddock.images.upload")}
								</Button>
							}
						/>
					</OfflineActionGuard>
				</div>

				{hasImages ? (
					<MediaGallery
						orgId={orgId}
						entityType="GrassOptzPaddock"
						entityId={paddockId}
					/>
				) : (
					<OfflineActionGuard blocked={blocked}>
						<ImageUploadDialog
							orgId={orgId}
							entityType="GrassOptzPaddock"
							entityId={paddockId}
							trigger={
								<button
									type="button"
									aria-disabled={blocked}
									className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-base-300 px-4 py-10 text-center text-sm text-base-content/60 transition-colors hover:border-primary/40 hover:bg-base-200/50"
								>
									{t("grass.paddock.images.dropzone")}
								</button>
							}
						/>
					</OfflineActionGuard>
				)}
			</div>
		</div>
	);
}
